import React, { useEffect, useState, useMemo, useCallback } from 'react';
import _ from 'lodash';

import { ImagePreview } from './ImagePreview';
import FiltersBar from '../common/filters-bar/FiltersBar';
import SingleTextEditor from '../common/editors/SingleTextEditor';
import { IconButton } from '../common/IconButton';
import LegoContextSummary from '../lego/LegoContextSummary';
import BadgeId from '../common/BadgeId';
import { simColor } from '../common/SimilarityBadge';


/** Builds a compact "modelId year" label from a cluster/vehicle context object (or array). */
function formatContext(ctx) {
  if (!ctx) return null;
  const list = _.isArray(ctx) ? ctx : [ctx];
  const c = list[0];
  if (!c || typeof c !== 'object') return null;
  let year = c.year;
  if (_.isArray(year)) year = year.join('/');
  const label = _.compact([c.modelId || c.make, year]).join(' ');
  if (!label) return null;
  return list.length > 1 ? `${label} +${list.length - 1}` : label;
}

/**
 * Embedding-based "most similar images" search & display, reusable across pages.
 *
 * Given a source `imgId` (or a random one), queries `services/data/image-embed-search`
 * and renders the query image alongside its nearest neighbours. Clicking a result drills
 * into it (re-searches from that image). The optional text/Random controls let the user
 * pick a different source.
 *
 * State (current source id, results) is owned internally; `onSourceChange` is fired
 * whenever the resolved source image changes so a parent can sync the url, etc.
 *
 * The source can also be an externally-supplied embedding vector (`queryVector`, the "embedding
 * weights") instead of a stored image id — e.g. a contribution photo freshly embedded by the
 * fusebox/embed endpoint. In that mode the optional `sourceUrl`/`sourceContext` are shown as the
 * query image. Clicking any result drills in by its stored image id, leaving vector mode.
 *
 * @param {Object}  props
 * @param {string}  [props.imgId]          Initial source image id ('' => random on mount).
 * @param {string}  [props.setup]          Initial embedding setup (setupName in images_embed; '' = all).
 * @param {number[]} [props.queryVector]   Raw embedding to search from instead of an image id.
 * @param {string}  [props.sourceUrl]      Query image url to display when searching from a vector.
 * @param {any}     [props.sourceContext]  Query image context to display when searching from a vector.
 * @param {number}  [props.limit]          Max neighbours to fetch.
 * @param {boolean} [props.showControls]   Show the image-id text field + Search/Random buttons.
 * @param {boolean} [props.searchOnMount]  Run an initial search when mounted.
 * @param {(imgId: string) => void} [props.onSourceChange]  Called with the resolved source id.
 * @param {(setup: string) => void} [props.onSetupChange]   Called when the user picks another setup.
 */
export default function ImageTopSimilarityFinder({
  imgId: initialImgId = '',
  setup: initialSetup = '',
  queryVector = null,
  sourceUrl = null,
  sourceContext = null,
  limit = 30,
  showControls = true,
  searchOnMount = true,
  onSourceChange = null,
  onSetupChange = null,
}) {
  const [imgId, setImgId] = useState(initialImgId);
  const [setup, setSetup] = useState(initialSetup);
  const [vector, setVector] = useState(queryVector); // external query embedding (the "embedding weights")
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchSvc = useMemo(() => client.service('services/data/image-embed-search'), []);

  const search = useCallback(async ({ random = false, id, setupOverride, useVector = false } = {}) => {
    const queryImgId = id !== undefined ? id : imgId;
    const querySetup = setupOverride !== undefined ? setupOverride : setup;
    // Any explicit id / random / text search leaves the external-vector mode behind.
    if (!useVector) setVector(null);
    setImgId(queryImgId);
    setLoading(true);
    setError(null);
    try {
      const result = await searchSvc.find({
        query: useVector
          ? { vector, sourceUrl, sourceContext, limit, setup: querySetup }
          : { imgId: random ? undefined : queryImgId, random, limit, setup: querySetup },
      });
      const newImgId = result?.source?.imgId || (useVector ? '' : queryImgId);
      setImgId(newImgId);
      setData(result);
      setLoading(false);
      onSourceChange && newImgId && onSourceChange(newImgId);
    } catch (err) {
      console.error(err);
      setError(err.toString());
      setLoading(false);
    }
  }, [imgId, setup, limit, searchSvc, onSourceChange, vector, sourceUrl, sourceContext]);

  const changeSetup = (newSetup) => {
    setSetup(newSetup);
    onSetupChange && onSetupChange(newSetup);
    // Re-search in the newly selected embedding space (keep vector mode if that's how we started).
    if (vector) search({ useVector: true, setupOverride: newSetup });
    else search({ id: imgId, setupOverride: newSetup });
  };

  // Initial load: search from the supplied vector, else the provided imgId, else a random image.
  useEffect(() => {
    if (searchOnMount) {
      if (queryVector) search({ useVector: true });
      else search({ random: !initialImgId, id: initialImgId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const source = data && data.source;
  const results = (data && data.results) || [];

  return <div className={''}>
    {showControls ? <FiltersBar>
      Image id:
      <div className='ml-2' style={{ width: '360px' }}>
        <SingleTextEditor
          small
          value={imgId}
          onChange={id => setImgId((id || '').trim())}
          onKeyDown={e => { if (e.key === 'Enter') search(); }}
        />
      </div>
      <IconButton icon={'search'} level={'primary'} onClick={() => search()}>Search</IconButton>
      <IconButton icon={'casino'} level={'secondary'} onClick={() => search({ random: true })}>Random</IconButton>
      <span className={'small text-muted ml-3'}>Embedding:</span>
      <select
        className={'form-control form-control-sm d-inline-block ml-1'}
        style={{ width: '280px' }}
        value={setup}
        onChange={e => changeSetup(e.target.value)}
      >
        <option value={''}>(default setup)</option>
        {_.map(data?.setups || [], s => <option key={s.setupName} value={s.setupName}>
          {s.setupName || '(unnamed)'} — {s.count} imgs
        </option>)}
      </select>
      {data ? <span className={'small text-muted ml-3'}>
        setup: <span className={'monospace'}>{data.setupName}</span> · dim {data.dim} · {results.length} matches
      </span> : null}
    </FiltersBar> : null}

    {error ? <div className={'alert alert-danger'}>{error}</div> : null}
    {loading ? <div className={'text-muted'}>Loading…</div> : null}

    {source ? <div className={'d-flex align-items-start'}>
      <div className={'mr-2 ml-2 mt-1 text-center'} style={{ flex: '0 0 auto' }}>
        <div className={'small text-muted mb-1'}>Query image</div>
        {
          source.url
            ?
            <div style={{ width: '500px', height: '500px' }}>
              <ImagePreview url={source.url}  context={source.context} fluidContain zoom className={'rounded bg-light border border-primary'} />
            </div>
            :
            <div className={'text-danger small'}>image url not found</div>
        }

        {
          formatContext(source.context)
            ? <div className={'mt-1'}>
              <LegoContextSummary context={source.context} />
            </div>
            :
            <div className={'small text-muted'}>no context</div>
        }
        {source.imgId ? <BadgeId id={source.imgId}>Image id:</BadgeId> : null}
      </div>

      <div style={{ flex: '1 1 auto' }}>
        <div className={'small text-muted mb-1'}>Most similar ({results.length})</div>
        <div className={'d-flex flex-wrap'}>
          {_.map(results, ({ imgId: rid, url, score, context }) => {
            return <span key={rid} className={'inline-block border rounded m-1 text-center bg-light'}>
              {url
                ? <ImagePreview url={url} context={context} img200 contain zoom />
                : <div className={'img-200 d-flex align-items-center justify-content-center text-danger small'}>no url</div>}

              <br />
              <BadgeId id={rid} />
              <div className={'btn d-block text-center pt-1 pb-0'} style={{ backgroundColor: simColor(score) }} onClick={() => search({ id: rid })}>
                <LegoContextSummary context={_.pick(context, 'modelId', 'year')} inline={true} />

                <div className={'small text-white px-1 rounded'}>
                  {score != null ? score.toFixed(4) : null}
                </div>
              </div>
            </span>;
          })}
        </div>
      </div>
    </div> : null}
  </div>;
}
